// src/components/billing/CreditBalanceCard.jsx
import React from 'react';
import { Typography, Button, Skeleton, Box, useTheme, Stack, Chip, alpha, Icon } from '@mui/material';
import { 
    AccountBalanceWalletOutlined as AccountBalanceWalletIcon, 
    AddCardOutlined as TopUpIcon, // Changed icon
    TrendingUpRounded as TrendingUpIcon, // Example for a potential future stat
} from '@mui/icons-material';
import CountUp from 'react-countup'; // npm install react-countup
import SectionCard from './SectionCard';

const CreditBalanceCard = ({ balance, isLoading, onViewPlansClick, lastTopUpAmount }) => {
  const theme = useTheme();

  // Determine color based on balance (example thresholds)
  const balanceColor = () => {
    if (isLoading || balance === undefined || balance === null) return theme.palette.text.primary;
    if (balance < 100) return theme.palette.warning.dark;
    if (balance < 1000) return theme.palette.info.dark;
    return theme.palette.success.dark;
  };
  
  const formattedBalance = (bal) => {
    if (bal === undefined || bal === null) return '0.00';
    // Show more precision for smaller balances
    return bal < 1 ? bal.toFixed(6) : bal.toFixed(2);
  }

  return (
    <SectionCard 
      title="Credit Balance" 
      icon={<AccountBalanceWalletIcon />} 
      fullHeight={true} // Ensure it takes available height
      contentSx={{ justifyContent: 'space-between' }} // Push button to bottom
    >
      <Box>
        {isLoading ? (
          <Stack spacing={1} sx={{my: 1, alignItems: 'flex-start'}}>
            <Skeleton variant="rectangular" width="60%" height={52} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="text" width="40%" height={24} />
            {lastTopUpAmount && <Skeleton variant="text" width="50%" height={20} sx={{mt: 1}}/>}
          </Stack>
        ) : balance !== undefined && balance !== null ? (
          <Box sx={{ textAlign: 'left', my: {xs: 1, sm: 1.5} }}>
            <Typography 
              variant="h1" 
              component="p" 
              sx={{ 
                fontWeight: 700, 
                color: balanceColor(), 
                lineHeight: 1, 
                mb: 0.5,
                letterSpacing: '-1px'
              }}
            >
              <CountUp 
                start={0} 
                end={balance} 
                duration={1.5} 
                decimals={balance < 1 ? 6 : 2} 
                separator=","
              />
            </Typography>
            <Typography variant="h6" component="span" color="text.secondary" sx={{ fontWeight: 500 }}>
              Available Credits
            </Typography>
            {/* Example of an additional piece of info */}
            {lastTopUpAmount && typeof lastTopUpAmount === 'number' && (
                <Chip 
                    icon={<TrendingUpIcon fontSize="small"/>}
                    label={`Last top-up: ${lastTopUpAmount.toFixed(0)} credits`}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{ mt: 1.5, display:'block', width:'fit-content', backgroundColor: alpha(theme.palette.info.main, 0.08) }}
                />
            )}
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{my:1.5, fontStyle: 'italic', textAlign:'center', py:3}}>
            Unable to load your current balance. Please try refreshing.
          </Typography>
        )}
      </Box>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={onViewPlansClick}
        sx={{ 
          mt: 2, // Ensure some margin from content above
          py: 1.5, 
          textTransform: 'none', 
          fontWeight: 'bold', 
          fontSize: '1rem',
          borderRadius: '12px', // Match other buttons
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
          '&:hover': {
            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
            transform: 'translateY(-1px)'
          }
        }}
        startIcon={<TopUpIcon />}
        disabled={isLoading}
      >
        View Plans & Add Credits
      </Button>
    </SectionCard>
  );
};

export default CreditBalanceCard;